import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { mockUploadFile } from "@/lib/catalog/mockCatalogApi";
import { PRODUCT_GALLERY_ATTR, PRODUCT_IMAGE_MAX_BYTES } from "@/lib/catalog/productMedia";
import { catalogProductErrorMessage, uploadProductImage } from "@/lib/catalog/productsStore";
import type { UploadedFile } from "@/lib/catalog/types";
import { normalizeProductMediaUrlForBrowser } from "@/lib/mediaUrls";
import { cn } from "@/lib/utils";
import { Camera, Check, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

type UploadTrack = {
  progress: number;
  status: "uploading" | "error";
  error?: string;
};

type ProductImageGalleryProps = {
  images: UploadedFile[];
  onChange: (images: UploadedFile[]) => void;
  /** When set, each file uploads immediately (one request per image). */
  productId?: string | null;
  disabled?: boolean;
  className?: string;
  dropzoneClassName?: string;
};

function reorderImages(images: UploadedFile[], fromId: string, toId: string): UploadedFile[] {
  if (fromId === toId) return images;
  const fromIndex = images.findIndex((img) => img.id === fromId);
  const toIndex = images.findIndex((img) => img.id === toId);
  if (fromIndex < 0 || toIndex < 0) return images;
  const next = [...images];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function ProductImageGallery({
  images,
  onChange,
  productId,
  disabled = false,
  className,
  dropzoneClassName,
}: ProductImageGalleryProps) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [dropActive, setDropActive] = useState(false);
  const [dragImageId, setDragImageId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [uploadTracks, setUploadTracks] = useState<Record<string, UploadTrack>>({});
  const [batchUpload, setBatchUpload] = useState<{ completed: number; total: number } | null>(null);
  const uploadingRef = useRef(false);

  const setTrack = useCallback((id: string, patch: UploadTrack | null) => {
    setUploadTracks((prev) => {
      if (patch === null) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: patch };
    });
  }, []);

  const uploadOne = useCallback(
    async (tempId: string, file: File): Promise<UploadedFile | null> => {
      setTrack(tempId, { progress: 0, status: "uploading" });
      try {
        if (productId) {
          const uploaded = await uploadProductImage(productId, file, (pct) => {
            setTrack(tempId, { progress: pct, status: "uploading" });
          });
          setTrack(tempId, null);
          return uploaded;
        }
        const local = await mockUploadFile(file, PRODUCT_GALLERY_ATTR);
        setTrack(tempId, null);
        return local;
      } catch (e) {
        const message = catalogProductErrorMessage(e);
        setTrack(tempId, { progress: 0, status: "error", error: message });
        toast.error(message);
        return null;
      }
    },
    [productId, setTrack],
  );

  const ingestFiles = useCallback(
    async (fileList: File[]) => {
      if (disabled || uploadingRef.current) return;
      const good = fileList.filter((f) => f.type.startsWith("image/") && f.size <= PRODUCT_IMAGE_MAX_BYTES);
      const skipped = fileList.length - good.length;
      if (skipped > 0) {
        toast.error(`${skipped} file(s) skipped — use images up to 10MB each.`);
      }
      if (good.length === 0) return;

      uploadingRef.current = true;
      setBatchUpload({ completed: 0, total: good.length });

      const placeholders: UploadedFile[] = good.map((file) => ({
        id: crypto.randomUUID(),
        filename: file.name,
        mimeType: file.type || "image/*",
        size: file.size,
        visibility: "public",
        url: URL.createObjectURL(file),
        localFile: productId ? undefined : file,
      }));

      let working = [...images, ...placeholders];
      onChange(working);

      let completed = 0;
      for (let i = 0; i < good.length; i++) {
        const file = good[i];
        const placeholder = placeholders[i];
        const uploaded = await uploadOne(placeholder.id, file);
        completed += 1;
        setBatchUpload({ completed, total: good.length });

        working = working.filter((img) => img.id !== placeholder.id);
        if (uploaded) {
          working = [...working, uploaded];
        }
        onChange(working);
      }

      setBatchUpload(null);
      uploadingRef.current = false;
      if (completed > 0 && completed === good.length) {
        toast.success(completed === 1 ? "Image uploaded." : `${completed} images uploaded.`);
      }
    },
    [disabled, images, onChange, productId, uploadOne],
  );

  const isUploading = batchUpload !== null;

  return (
    <div className={cn("space-y-3", className)}>
      <input
        ref={imageInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/*"
        className="hidden"
        disabled={disabled || isUploading}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.currentTarget.value = "";
          void ingestFiles(files);
        }}
      />

      <button
        type="button"
        disabled={disabled || isUploading}
        className={cn(
          "w-full rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-60",
          dropActive ? "border-primary bg-primary/10" : "border-border bg-muted/20 hover:bg-muted/35 hover:border-primary/45",
          dropzoneClassName,
        )}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!disabled && !isUploading) setDropActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          const next = e.relatedTarget as Node | null;
          if (!next || !e.currentTarget.contains(next)) setDropActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDropActive(false);
          if (disabled || isUploading) return;
          void ingestFiles(Array.from(e.dataTransfer.files));
        }}
        onClick={() => imageInputRef.current?.click()}
      >
        <Camera className="w-9 h-9 mx-auto text-muted-foreground/80 mb-2" aria-hidden />
        <p className="text-sm font-medium text-foreground">Drop images here or browse (multiple)</p>
        <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP — up to 10MB each · no limit on count</p>
      </button>

      {images.length > 0 ? (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-muted-foreground">Drag any image to reorder the gallery.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((u, index) => {
              const track = uploadTracks[u.id];
              const isDragging = dragImageId === u.id;
              const isDropTarget = dropTargetId === u.id && dragImageId !== u.id;
              const previewUrl = normalizeProductMediaUrlForBrowser(u.url) ?? u.url;
              const canReorder = !disabled && !isUploading;

              return (
                <div
                  key={u.id}
                  draggable={canReorder}
                  className={cn(
                    "relative group aspect-[4/3] rounded-lg overflow-hidden border bg-muted/30 transition-shadow",
                    canReorder && "cursor-grab active:cursor-grabbing",
                    isDragging && "opacity-50 ring-2 ring-primary/40",
                    isDropTarget && "ring-2 ring-primary shadow-md",
                    track?.status === "error" ? "border-destructive/60" : "border-border",
                  )}
                  onDragStart={(e) => {
                    if (!canReorder) {
                      e.preventDefault();
                      return;
                    }
                    setDragImageId(u.id);
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", u.id);
                  }}
                  onDragEnd={() => {
                    setDragImageId(null);
                    setDropTargetId(null);
                  }}
                  onDragOver={(e) => {
                    if (!dragImageId || dragImageId === u.id) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDropTargetId(u.id);
                  }}
                  onDragLeave={() => {
                    if (dropTargetId === u.id) setDropTargetId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (dragImageId && dragImageId !== u.id) {
                      onChange(reorderImages(images, dragImageId, u.id));
                    }
                    setDragImageId(null);
                    setDropTargetId(null);
                  }}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground p-2 text-center">
                      {u.filename}
                    </div>
                  )}

                  <span className="absolute top-1.5 left-1.5 rounded bg-background/85 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-foreground shadow-sm pointer-events-none">
                    {index + 1}
                  </span>

                  {track?.status === "uploading" ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-end bg-background/55 p-2 gap-1.5 pointer-events-none">
                      <Progress value={track.progress} className="h-1.5 w-full bg-background/80" />
                      <span className="text-[10px] font-medium text-foreground tabular-nums">{track.progress}%</span>
                    </div>
                  ) : null}

                  {track?.status === "error" ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-destructive/15 p-2 text-center pointer-events-none">
                      <span className="text-[10px] font-medium text-destructive line-clamp-3">{track.error ?? "Upload failed"}</span>
                    </div>
                  ) : null}

                  {!track ? (
                    <span
                      className="absolute top-1.5 right-1.5 z-10 inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white shadow-md ring-2 ring-white/90 pointer-events-none"
                      title="Uploaded"
                      aria-label="Uploaded"
                    >
                      <Check className="h-4 w-4 stroke-[2.5]" aria-hidden />
                    </span>
                  ) : null}

                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute bottom-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={disabled || isUploading}
                    onPointerDown={(ev) => {
                      // Keep remove clickable without starting a reorder drag.
                      ev.stopPropagation();
                      ev.preventDefault();
                    }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onChange(images.filter((x) => x.id !== u.id));
                    }}
                    aria-label={`Remove ${u.filename}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
